import { useEffect, useRef, useState } from "react";

import {
  borrarDocumento,
  crearDocumento,
  guardarCompartirDoc,
  guardarDocumento,
  miMail,
  misDocumentos,
  traerDocumento,
} from "../services/espacio";
import CompartirPanel from "./CompartirPanel";
import Loading from "./Loading";

import "./css/espacio.css";

/* Mis documentos: la otra mitad de "Mi espacio".
 *
 * La planilla es para lo que tiene forma de tabla; esto es para lo que no —una
 * lista de pendientes, notas de una reunión, un instructivo—. Texto libre, se
 * guarda solo, es tuyo (RLS por dueño) y nosotros nos alimentamos desde el
 * servidor. Se comparte igual que las planillas: el dueño elige, el invitado
 * lee, y CEPI va fijo (transparencia).
 */

const TOPE = 100000; // el límite de la base

/* ── El editor de un documento ─────────────────────────────────────────────── */

function Editor({ id, yo, onSalir, onCambioTitulo }) {
  const [titulo, setTitulo] = useState("");
  const [contenido, setContenido] = useState(null);
  const [meta, setMeta] = useState({ dueno: "", compartidaCon: [] });
  const [estado, setEstado] = useState("cargando");
  const cargado = contenido !== null;
  const esMio = !!meta.dueno && meta.dueno.toLowerCase() === (yo || "").toLowerCase();

  useEffect(() => {
    let vivo = true;
    traerDocumento(id)
      .then((d) => {
        if (!vivo) return;
        setTitulo(d.titulo || "Sin título");
        setContenido(d.contenido || "");
        setMeta({ dueno: d.dueno || "", compartidaCon: d.compartida_con || [] });
        setEstado("guardado");
      })
      .catch(() => vivo && setEstado("error"));
    return () => { vivo = false; };
  }, [id]);

  const primera = useRef(true);
  useEffect(() => {
    if (!cargado || !esMio) return undefined;
    if (primera.current) { primera.current = false; return undefined; }
    setEstado("guardando");
    const t = setTimeout(() => {
      guardarDocumento(id, titulo, contenido)
        .then(() => setEstado("guardado"))
        .catch(() => setEstado("error"));
    }, 800);
    return () => clearTimeout(t);
  }, [titulo, contenido, cargado, esMio, id]);

  if (!cargado) return estado === "error"
    ? <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    : <Loading />;

  return (
    <div className="docs">
      <div className="esp-acciones">
        <button className="rto-link" onClick={onSalir}>← Mis documentos</button>
        <input
          aria-label="Título del documento" value={titulo} readOnly={!esMio}
          onChange={(e) => { setTitulo(e.target.value); onCambioTitulo?.(id, e.target.value); }}
          style={{ fontWeight: 600, fontSize: 16, flex: 1, minWidth: 140, background: "transparent", color: "inherit", border: "1px solid rgba(128,128,128,.3)", borderRadius: 6, padding: "5px 8px" }}
        />
        {esMio && (
          <span className="esp-guardado">
            {estado === "guardando" ? "guardando…" : estado === "error" ? "no se pudo guardar" : "guardado ✓"}
          </span>
        )}
      </div>

      {!esMio && (
        <p className="panel-aviso suave">
          Te compartió este documento <b>{meta.dueno}</b>. Podés leerlo, pero no
          editarlo — es de quien lo creó.
        </p>
      )}

      {esMio && (
        <CompartirPanel
          compartidaCon={meta.compartidaCon} yo={yo} que="el documento"
          onGuardar={(mails) => guardarCompartirDoc(id, mails)}
        />
      )}

      <textarea
        className="esp-doc-area" aria-label="Contenido del documento"
        value={contenido} maxLength={TOPE} readOnly={!esMio}
        placeholder="Escribí lo que quieras — pendientes, notas, contactos, un instructivo…"
        onChange={(e) => setContenido(e.target.value)}
      />

      {esMio && (
        <div className="esp-acciones">
          <span className="tenue">{contenido.length.toLocaleString("es-AR")} / {TOPE.toLocaleString("es-AR")} caracteres</span>
          <button
            className="esp-btn" style={{ marginLeft: "auto", color: "var(--crit, #c0392b)" }}
            onClick={() => {
              if (!window.confirm(`¿Borrar el documento "${titulo}"? No se puede deshacer.`)) return;
              borrarDocumento(id).then(onSalir).catch(() => setEstado("error"));
            }}
          >
            Borrar documento
          </button>
        </div>
      )}

      <p className="docs-pie">Se guarda solo.</p>
    </div>
  );
}

/* ── El índice ─────────────────────────────────────────────────────────────── */

const MisDocumentos = () => {
  const [lista, setLista] = useState(null);
  const [yo, setYo] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [error, setError] = useState(null);

  const recargar = () =>
    misDocumentos().then(setLista).catch((e) => setError(e.message || "No se pudo leer"));
  useEffect(() => {
    miMail().then((m) => setYo((m || "").toLowerCase())).catch(() => setYo(""));
    recargar();
  }, []);

  const nuevo = async () => {
    try {
      setAbierto(await crearDocumento("Sin título"));
    } catch (e) {
      setError(e.message || "No se pudo crear");
    }
  };

  if (abierto) {
    return (
      <Editor
        id={abierto} yo={yo}
        onSalir={() => { setAbierto(null); recargar(); }}
        onCambioTitulo={(id, titulo) =>
          setLista((l) => (l || []).map((d) => (d.id === id ? { ...d, titulo } : d)))}
      />
    );
  }

  if (error) return <div className="panel-aviso"><h3>No se pudo consultar</h3></div>;
  if (lista === null || yo === null) return <Loading />;

  const mios = lista.filter((d) => (d.dueno || "").toLowerCase() === yo);
  const compartidos = lista.filter((d) => (d.dueno || "").toLowerCase() !== yo);

  const Item = ({ d, deQuien }) => (
    <div className="esp-item" onClick={() => setAbierto(d.id)}>
      <b>{d.titulo || "Sin título"}</b>
      <span className="tenue">
        {deQuien ? `de ${deQuien}` : (d.actualizado_en ? new Date(d.actualizado_en).toLocaleDateString("es-AR") : "")}
      </span>
    </div>
  );

  return (
    <div className="docs">
      <div className="esp-acciones">
        <h2 style={{ margin: 0, flex: 1 }}>Mis documentos</h2>
        <button className="btn-primario" onClick={nuevo}>+ Nuevo documento</button>
      </div>

      {mios.length === 0 ? (
        <p className="panel-aviso suave">
          Todavía no tenés ningún documento.
        </p>
      ) : (
        <div className="esp-lista">
          {mios.map((d) => <Item key={d.id} d={d} />)}
        </div>
      )}

      {compartidos.length > 0 && (
        <>
          <h3 style={{ margin: "22px 0 6px" }}>Compartidos conmigo</h3>
          <div className="esp-lista">
            {compartidos.map((d) => <Item key={d.id} d={d} deQuien={d.dueno} />)}
          </div>
        </>
      )}
    </div>
  );
};

export default MisDocumentos;
